// ===================== The Fang Tree (meta skill tree) =====================
// Permanent, cross-run power bought with banked coins — laid out as a
// constellation with three branches that converge on an apex:
//   FANG (offense) · HIDE (defense) · FORTUNE (economy)
// Nodes unlock when their `requires` levels are met; buying every keystone
// opens the Apex Predator capstone, and owning THAT unlocks ASCENSION:
// reset the whole tree in exchange for a permanent, stacking prestige bonus.
// metaBonuses() aggregates purchased levels (and Ascensions) into the bonus
// object the Player constructor understands.
import { Save } from '../engine/save.js';

// Per-Ascension permanent bonus (applied on top of the tree, forever).
export const PRESTIGE_BONUS = { damage: 0.05, xp: 0.05, gold: 0.10 };

// The rite's toll: ◈400, growing ×1.5 per Ascension already held. Modelled
// against the income curve: a Threat-X Void Lair banks ~20,800 a run while a
// full tree rebuy costs ~5,150, so with no toll one endgame run funded FOUR
// whole prestige cycles and Ascension degenerated into per-run clicking. With
// it, a cycle stays cheaper than a run through ~prestige 5, costs about a run
// near 10, and becomes a genuine chase in the teens — the same super-linearity
// rule the Threat ladder follows, and for the same reason: Ascension stacks
// power forever, so any recurring gate must outgrow linear income.
export function ascensionCost() {
  return Math.round(400 * Math.pow(1.5, Save.data.prestige || 0));
}

// pos = [x%, y%] inside the tree canvas (0,0 = top-left). requires =
// [[nodeId, minLevel], …] — every entry must be met before the node unlocks.
export const SKILL_TREE = [
  // ---- FANG — offense (magenta) ----
  {
    id: 'power', branch: 'fang', pos: [16, 84], requires: [],
    name: 'Power', icon: '⚔', max: 10,
    desc: (l) => `+${(l + 1) * 6}% damage`,
    cost: (l) => 15 + l * 14,
    apply: (b, l) => { b.damage += l * 0.06; },
  },
  {
    id: 'focus', branch: 'fang', pos: [8, 60], requires: [['power', 2]],
    name: 'Focus', icon: '✷', max: 6,
    desc: (l) => `+${(l + 1) * 2}% crit chance`,
    cost: (l) => 18 + l * 16,
    apply: (b, l) => { b.crit += l * 0.02; },
  },
  {
    id: 'rend', branch: 'fang', pos: [25, 55], requires: [['power', 3]],
    name: 'Rend', icon: '🗡', max: 5,
    desc: (l) => `+${(l + 1) * 5}% crit damage`,
    cost: (l) => 30 + l * 24,
    apply: (b, l) => { b.critMult += l * 0.05; },
  },
  {
    id: 'wildhunt', branch: 'fang', pos: [17, 38], requires: [['focus', 3], ['rend', 3]],
    name: 'Wild Hunt', icon: '⋔', max: 1, keystone: true,
    desc: () => 'KEYSTONE — every weapon fires +1 projectile',
    cost: () => 260,
    apply: (b, l) => { b.projAdd += l; },
  },

  // ---- HIDE — defense (cyan) ----
  {
    id: 'vigor', branch: 'hide', pos: [50, 86], requires: [],
    name: 'Vigor', icon: '♥', max: 10,
    desc: (l) => `+${(l + 1) * 10} max HP`,
    cost: (l) => 12 + l * 10,
    apply: (b, l) => { b.maxHp += l * 10; },
  },
  {
    id: 'guard', branch: 'hide', pos: [40, 65], requires: [['vigor', 2]],
    name: 'Guard', icon: '🛡', max: 6,
    desc: (l) => `+${l + 1} armor`,
    cost: (l) => 18 + l * 18,
    apply: (b, l) => { b.armor += l; },
  },
  {
    id: 'mend', branch: 'hide', pos: [60, 65], requires: [['vigor', 2]],
    name: 'Mend', icon: '✚', max: 6,
    desc: (l) => `+${((l + 1) * 0.4).toFixed(1)} HP/sec regen`,
    cost: (l) => 20 + l * 16,
    apply: (b, l) => { b.regen += l * 0.4; },
  },
  {
    id: 'phoenix', branch: 'hide', pos: [50, 38], requires: [['guard', 3], ['mend', 3]],
    name: 'Phoenix', icon: '✵', max: 1, keystone: true,
    desc: () => 'KEYSTONE — start each run with 1 revive',
    cost: () => 200,
    apply: (b, l) => { b.revive += l; },
  },

  // ---- FORTUNE — economy (gold) ----
  {
    id: 'greed', branch: 'fortune', pos: [84, 84], requires: [],
    name: 'Greed', icon: '◈', max: 6,
    desc: (l) => `+${(l + 1) * 8}% gold`,
    cost: (l) => 14 + l * 12,
    apply: (b, l) => { b.gold += l * 0.08; },
  },
  {
    id: 'scholar', branch: 'fortune', pos: [75, 55], requires: [['greed', 2]],
    name: 'Scholar', icon: '✧', max: 6,
    desc: (l) => `+${(l + 1) * 6}% XP`,
    cost: (l) => 16 + l * 14,
    apply: (b, l) => { b.xp += l * 0.06; },
  },
  {
    id: 'clover', branch: 'fortune', pos: [92, 61], requires: [['greed', 3]],
    name: 'Clover', icon: '🍀', max: 3,
    desc: (l) => `+${l + 1} luck (better cards & drops)`,
    cost: (l) => 40 + l * 40,
    apply: (b, l) => { b.luck += l; },
  },
  {
    id: 'midas', branch: 'fortune', pos: [83, 38], requires: [['scholar', 3], ['clover', 2]],
    name: 'Midas Fang', icon: '👑', max: 1, keystone: true,
    desc: () => 'KEYSTONE — +25% gold and +1 luck',
    cost: () => 220,
    apply: (b, l) => { b.gold += 0.25 * l; b.luck += l; },
  },

  // ---- Cross-branch utility ----
  {
    id: 'boots', branch: 'hide', pos: [33, 79], requires: [['power', 1], ['vigor', 1]],
    name: 'Swift', icon: '»', max: 6,
    desc: (l) => `+${(l + 1) * 4}% move speed`,
    cost: (l) => 15 + l * 12,
    apply: (b, l) => { b.moveSpeed += l * 0.04; },
  },
  {
    id: 'lodestone', branch: 'fortune', pos: [67, 79], requires: [['vigor', 1], ['greed', 1]],
    name: 'Lodestone', icon: '🧲', max: 5,
    desc: (l) => `+${(l + 1) * 12}% pickup range`,
    cost: (l) => 12 + l * 10,
    apply: (b, l) => { b.magnet += l * 0.12; },
  },

  // ---- Endless Hunger — the open-ended coin sink ----
  // Unlimited ranks with a superlinear cost curve so banked coins always have
  // somewhere meaningful to go: around rank 15–25 a single next rank costs an
  // endgame session's income (~2,000–9,000/hr). `persist: true` means
  // Ascension's tree reset never touches it (see ascendPreserving()).
  {
    id: 'hunger', branch: 'apex', pos: [50, 52], requires: [],
    name: 'Endless Hunger', icon: '∞', max: Infinity, repeatable: true, persist: true,
    desc: (l) => `+${l + 1}% damage — no rank limit, and Ascension never resets it.`,
    cost: (l) => Math.round(200 * Math.pow(1.25, l)),
    apply: (b, l) => { b.damage += l * 0.01; },
  },

  // ---- Apex ----
  {
    id: 'apexpredator', branch: 'apex', pos: [50, 12],
    requires: [['wildhunt', 1], ['phoenix', 1], ['midas', 1]],
    name: 'Apex Predator', icon: '🐺', max: 1, apex: true,
    desc: () => 'APEX — +10% damage, +50 max HP, +10% XP. Owning the apex lets you ASCEND.',
    cost: () => 400,
    apply: (b, l) => { b.damage += 0.10 * l; b.maxHp += 50 * l; b.xp += 0.10 * l; },
  },
];

export const META_BY_ID = Object.fromEntries(SKILL_TREE.map((u) => [u.id, u]));
// Back-compat alias (older callers iterate META_UPGRADES).
export const META_UPGRADES = SKILL_TREE;

// Are a node's prerequisites met?
export function nodeUnlocked(node) {
  return node.requires.every(([id, lvl]) => Save.metaLevel(id) >= lvl);
}

// Aggregate purchased tree levels + Ascension bonuses into Player's bonus object.
export function metaBonuses() {
  const b = {
    maxHp: 0, moveSpeed: 0, damage: 0, crit: 0, critMult: 0, armor: 0, regen: 0,
    luck: 0, xp: 0, gold: 0, magnet: 0, revive: 0, projAdd: 0,
  };
  for (const u of SKILL_TREE) {
    const lvl = Save.metaLevel(u.id);
    if (lvl > 0) u.apply(b, lvl);
  }
  const p = Save.data.prestige || 0;
  if (p > 0) {
    b.damage += PRESTIGE_BONUS.damage * p;
    b.xp += PRESTIGE_BONUS.xp * p;
    b.gold += PRESTIGE_BONUS.gold * p;
  }
  return b;
}

// Cost to buy the NEXT level of a node (or null if maxed).
// Repeatable nodes (max: Infinity) never return null.
export function nextCost(u) {
  const lvl = Save.metaLevel(u.id);
  return lvl >= u.max ? null : u.cost(lvl);
}

// ---- BUY MAX (the post-Ascension rebuy sweep) ----
// Ascension wipes the tree, and rebuying it by hand is ~73 clicks. BUY MAX
// repeatedly takes the cheapest affordable next rank among unlocked,
// rank-LIMITED nodes. Endless Hunger (max: Infinity) is excluded by
// construction — its unlimited ranks would eat the whole bank.

// The cheapest next rank `coins` can buy right now, or null. `levelOf` lets
// the planner look ahead of the real save state; execution uses the default
// (real) levels. Prerequisites order themselves: a node only enters the pool
// once the levels `levelOf` reports meet its requirements.
export function cheapestBuy(coins, levelOf = (id) => Save.metaLevel(id)) {
  let best = null;
  for (const u of SKILL_TREE) {
    if (!Number.isFinite(u.max)) continue;                     // Endless Hunger
    const lvl = levelOf(u.id);
    if (lvl >= u.max) continue;
    if (!u.requires.every(([id, min]) => levelOf(id) >= min)) continue;
    const cost = u.cost(lvl);
    if (cost <= coins && (!best || cost < best.cost)) best = { id: u.id, cost };
  }
  return best;
}

// Preview what a BUY MAX sweep of `coins` would do: how many ranks, and the
// exact total spend. Pure — reads the save, never writes it. The caller shows
// this in the confirm, then executes through the normal spend/buy path.
export function buyMaxPlan(coins) {
  const planned = {};
  const levelOf = (id) => (id in planned ? planned[id] : Save.metaLevel(id));
  let ranks = 0, total = 0;
  for (;;) {
    const pick = cheapestBuy(coins - total, levelOf);
    if (!pick) break;
    planned[pick.id] = levelOf(pick.id) + 1;
    total += pick.cost;
    ranks++;
  }
  return { ranks, total };
}

// ASCENSION wipes the tree (Save.ascend), but nodes flagged `persist`
// (Endless Hunger) survive the reset — sunk coins there are never taken back.
// Ranks live in the generic save `upgrades` map, so no save-format change.
export function ascendPreserving() {
  const kept = [];
  for (const u of SKILL_TREE) {
    if (!u.persist) continue;
    const lvl = Save.metaLevel(u.id);
    if (lvl > 0) kept.push([u.id, lvl]);
  }
  const prestige = Save.ascend();
  for (const [id, lvl] of kept) Save.data.upgrades[id] = lvl;
  if (kept.length) Save.flush();
  return prestige;
}
